import { nanoid } from 'nanoid';
import { imageModels } from '@flowmuse/models/image';
import { videoModels } from '@flowmuse/models/video';
import { speechModels } from '@flowmuse/models/speech';
import {
  getMediaDurationFromUrl,
  type InlineAttachment,
} from '@flowmuse/models/inline-attachments';

// Drag payload MIME for inspiration cards (mirrors application/x-flowmuse-asset & friends).
export const INSPIRATION_MIME = 'application/x-flowmuse-inspiration';

// Click-to-use event: the inspiration panel dispatches this and the canvas
// (NodeDropzoneProvider) creates a prefilled node at the viewport center.
export const USE_INSPIRATION_EVENT = 'flowmuse:use-inspiration';

// 站外入口(如主页灵感库卡片的「在画布打开」)一次性接力 key:写入后跳转到新建的画布项目,
// 由 NodeDropzoneProvider 挂载时读取并清空(无 pending 缓冲,只此一次)。
export const CANVAS_PENDING_INSPIRATION_KEY = 'flowmuse:canvas-pending-inspiration';

/**
 * Serializable payload carried by both the drag dataTransfer and the
 * click-to-use event. The prompt/title are already localized by the panel so
 * the canvas side needs no i18n context.
 */
export type InspirationNodePayload = {
  kind: string; // 'image' | 'video' | 'music' | 'audio'
  prompt: string;
  title?: string;
  modelId?: string;
  mode?: string;
  aspectRatio?: string;
  resolution?: string;
  duration?: string;
  references?: { kind: string; url: string }[];
};

/** 灵感条目 → 画布/Studio 通用负载。调用方负责按当前语言取出 title/prompt(避免这里依赖 i18n)。 */
export function toInspirationNodePayload(
  item: {
    kind: string;
    modelId: string | null;
    mode: string | null;
    aspectRatio: string | null;
    resolution: string | null;
    duration: string | null;
    references: { kind: string; url: string }[] | null;
  },
  localized: { title: string; prompt: string },
): InspirationNodePayload {
  return {
    kind: item.kind,
    prompt: localized.prompt,
    title: localized.title || undefined,
    modelId: item.modelId ?? undefined,
    mode: item.mode ?? undefined,
    aspectRatio: item.aspectRatio ?? undefined,
    resolution: item.resolution ?? undefined,
    duration: item.duration ?? undefined,
    references: item.references?.length ? item.references : undefined,
  };
}

const MIME_BY_KIND: Record<string, string> = {
  image: 'image/png',
  video: 'video/mp4',
  audio: 'audio/mpeg',
};

/** references → InlineAttachment[]（同步；视频/音频时长由 measureInspirationDurations 后台补测）。 */
function referencesToAttachments(
  refs: { kind: string; url: string }[],
  allow: Set<'image' | 'video' | 'audio'>,
): InlineAttachment[] {
  return refs
    .filter(
      (r): r is { kind: 'image' | 'video' | 'audio'; url: string } =>
        Boolean(r?.url) && allow.has(r.kind as 'image' | 'video' | 'audio'),
    )
    .map((r) => ({
      id: nanoid(),
      url: r.url,
      type: MIME_BY_KIND[r.kind] ?? 'application/octet-stream',
      fileType: r.kind,
    }));
}

/**
 * 把灵感条目映射成一个"可直接运行"的画布生成节点（同步，节点即刻出现）：
 * - kind image → image transform 节点（instructions/model/imageMode/size/image_resolution + 参考图）
 * - kind video → video transform 节点（instructions/model/frameMode/aspectRatio/duration + 首尾帧/参考素材）
 * - kind music|audio → audio transform 节点（instructions/model + 参考音频）
 * 模型不在画布注册表时省略 model（节点回退到 preset/last/default）；模式/参数不合法由
 * 各 transform 的既有归一化逻辑兜底，不在此处重复校验。
 */
export function buildInspirationNode(payload: InspirationNodePayload): {
  nodeType: 'image' | 'video' | 'audio';
  data: Record<string, unknown>;
} {
  const refs = Array.isArray(payload.references) ? payload.references : [];
  const base: Record<string, unknown> = { viewMode: 'transform' };
  if (payload.prompt) base.instructions = payload.prompt;
  if (payload.title) base.name = payload.title.slice(0, 64);

  if (payload.kind === 'video') {
    if (payload.modelId && videoModels[payload.modelId]) base.model = payload.modelId;
    const attachments = referencesToAttachments(refs, new Set(['image', 'video', 'audio']));
    const hasImageRef = attachments.some((a) => a.fileType === 'image');
    const frameMode = payload.mode || (hasImageRef ? 'first' : undefined);
    if (frameMode) base.frameMode = frameMode;
    if (payload.aspectRatio) base.aspectRatio = payload.aspectRatio;
    const dur = Number(payload.duration);
    if (Number.isFinite(dur) && dur > 0) base.duration = dur;
    if (attachments.length > 0) {
      // 首/尾帧角色按 frameMode 预标注；其余图片作为参考。transform 的 role 归一化会兜底。
      let seenImages = 0;
      for (const a of attachments) {
        if (a.fileType !== 'image') continue;
        seenImages += 1;
        if (seenImages === 1 && (frameMode === 'first' || frameMode === 'first_last')) {
          a.role = 'start';
        } else if (seenImages === 2 && frameMode === 'first_last') {
          a.role = 'end';
        } else {
          a.role = 'reference';
        }
      }
      base.inlineAttachments = attachments;
    }
    return { nodeType: 'video', data: base };
  }

  if (payload.kind === 'music' || payload.kind === 'audio') {
    if (payload.modelId && speechModels[payload.modelId]) base.model = payload.modelId;
    const attachments = referencesToAttachments(refs, new Set(['audio']));
    if (attachments.length > 0) base.inlineAttachments = attachments;
    return { nodeType: 'audio', data: base };
  }

  // image（默认）
  if (payload.modelId && imageModels[payload.modelId]) base.model = payload.modelId;
  const attachments = referencesToAttachments(refs, new Set(['image']));
  const mode =
    payload.mode === 't2i' || payload.mode === 'i2i'
      ? payload.mode
      : attachments.length > 0
        ? 'i2i'
        : undefined;
  if (mode) base.imageMode = mode;
  if (payload.aspectRatio) base.size = payload.aspectRatio;
  if (payload.resolution) base.image_resolution = payload.resolution;
  if (attachments.length > 0) {
    for (const a of attachments) a.role = 'reference';
    base.inlineAttachments = attachments;
  }
  return { nodeType: 'image', data: base };
}

/**
 * 后台补测参考视频/音频的时长（节点侧的总时长校验依赖 attachment.duration）。
 * 不阻塞节点创建；无需要测量的附件时返回 null。返回 attachmentId → 秒数。
 */
export async function measureInspirationDurations(
  attachments: InlineAttachment[] | undefined,
): Promise<Map<string, number> | null> {
  const targets = (attachments ?? []).filter(
    (a) => (a.fileType === 'video' || a.fileType === 'audio') && !a.duration,
  );
  if (targets.length === 0) return null;
  const entries = await Promise.all(
    targets.map(async (a) => {
      try {
        const dur = await getMediaDurationFromUrl(a.url, a.fileType as 'video' | 'audio');
        return dur > 0 ? ([a.id, dur] as const) : null;
      } catch {
        return null;
      }
    }),
  );
  const measured = entries.filter((e): e is readonly [string, number] => e !== null);
  return measured.length > 0 ? new Map(measured) : null;
}
